import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Product,
  Transaction,
  PosConfig,
  Customer,
  Supplier,
  Salesman,
  ServicePoint,
  Coupon,
  Recipe,
  Category,
  UnitGroup,
  AttributeDef,
  CashFlowEntry,
  StockMovement,
  AppToggleSettings,
  TransportInfo,
  OnlineOrder,
  ActiveShift,
  ShiftRecord,
} from '../types';
import * as db from '../services/db';
import { opUpsert, opDelete, opSet, opAdjustStock, COLLECTION_CHANGED } from '../services/ops';
import { isOn, choiceOf } from '../services/appSettings';
import { applyTheme } from '../services/theme';

interface GoodsReceiveLine {
  productId: string;
  quantity: number;
  // Receive / issue against one variant of the product. Omitted for products
  // that have no variants.
  variantId?: string;
  variantName?: string;
}

interface PosContextValue {
  config: PosConfig;
  appSettings: AppToggleSettings;
  // Read a toggle (true/false) honoring documented defaults.
  isOn: (key: string) => boolean;
  // Read a radio/spinner choice honoring documented defaults.
  choice: (key: string) => string;
  updateAppSettings: (s: AppToggleSettings) => void;
  products: Product[];
  sales: Transaction[];
  customers: Customer[];
  suppliers: Supplier[];
  categories: Category[];
  cashflow: CashFlowEntry[];
  movements: StockMovement[];

  // Config
  updateConfig: (config: PosConfig) => void;

  // Products
  addProduct: (product: Omit<Product, 'id'>) => void;
  updateProduct: (product: Product) => void;
  deleteProduct: (id: string) => void;
  getProductByBarcode: (barcode: string) => Product | undefined;

  // Customers
  addCustomer: (customer: Omit<Customer, 'id'>) => Customer;
  updateCustomer: (customer: Customer) => void;
  deleteCustomer: (id: string) => void;

  // Suppliers
  addSupplier: (supplier: Omit<Supplier, 'id'>) => void;
  updateSupplier: (supplier: Supplier) => void;
  deleteSupplier: (id: string) => void;

  // Salesmen / employees
  salesmen: Salesman[];
  addSalesman: (salesman: Omit<Salesman, 'id'>) => void;
  updateSalesman: (salesman: Salesman) => void;
  deleteSalesman: (id: string) => void;

  // Saved transport vehicles (for the Transport Detail autocomplete)
  vehicles: TransportInfo[];
  saveVehicle: (info: TransportInfo) => void;

  // Online delivery orders (Swiggy/Zomato/own — manually entered in the port)
  onlineOrders: OnlineOrder[];
  addOnlineOrder: (order: Omit<OnlineOrder, 'id' | 'createdAt' | 'updatedAt'>) => OnlineOrder;
  updateOnlineOrder: (order: OnlineOrder) => void;
  deleteOnlineOrder: (id: string) => void;

  // Service points
  servicePoints: ServicePoint[];
  addServicePoint: (sp: Omit<ServicePoint, 'id'>) => void;
  updateServicePoint: (sp: ServicePoint) => void;
  deleteServicePoint: (id: string) => void;

  // Coupons
  coupons: Coupon[];
  addCoupon: (coupon: Omit<Coupon, 'id'>) => void;
  updateCoupon: (coupon: Coupon) => void;
  deleteCoupon: (id: string) => void;

  // Recipes / bill-of-materials
  recipes: Recipe[];
  addRecipe: (recipe: Omit<Recipe, 'id'>) => void;
  updateRecipe: (recipe: Recipe) => void;
  deleteRecipe: (id: string) => void;

  // Categories
  addCategory: (category: Omit<Category, 'id'>) => void;
  updateCategory: (category: Category) => void;
  deleteCategory: (id: string) => void;
  unitGroups: UnitGroup[];
  addUnitGroup: (group: Omit<UnitGroup, 'id'>) => void;
  updateUnitGroup: (group: UnitGroup) => void;
  deleteUnitGroup: (id: string) => void;

  // Attribute master (Size / Colour / Flavour ...) used by the Product Master
  attributes: AttributeDef[];
  addAttribute: (attribute: Omit<AttributeDef, 'id'>) => void;
  updateAttribute: (attribute: AttributeDef) => void;
  deleteAttribute: (id: string) => void;

  // Sales — assigns a sequential bill number and returns the finalized sale.
  recordSale: (sale: Transaction) => Promise<Transaction>;
  // Replace an existing sale (Edit Bill) and reconcile stock by the qty delta.
  updateSale: (updated: Transaction) => void;
  voidSale: (id: string) => void;
  recordSaleReturn: (originalBillNo: string, returnedItems: Transaction['items'], note?: string) => void;

  // Inventory
  receiveGoods: (lines: GoodsReceiveLine[], supplierId?: string, note?: string) => void;
  stockOut: (lines: GoodsReceiveLine[], reason: string) => void;

  // CashFlow
  addCashFlow: (entry: Omit<CashFlowEntry, 'id' | 'timestamp'>) => void;

  // Shift sessions (Android Shift bean) — one open shift at a time; bills are
  // stamped with its id. Closing snapshots a ShiftRecord and can start the next.
  activeShift: ActiveShift | null;
  openShift: (openingCash: number) => Promise<ActiveShift>;
  closeShift: (record: ShiftRecord, startNext: boolean, nextOpeningCash?: number) => void;

  // Helpers
  formatMoney: (amount: number) => string;
}

const PosContext = createContext<PosContextValue | undefined>(undefined);

// Config shape shown before the stored config has hydrated (matches db's
// DEFAULT_CONFIG); replaced by the real values once loading completes.
const INITIAL_CONFIG: PosConfig = {
  storeName: 'EPOS',
  currencySymbol: '₹',
  taxRatePercent: 5,
};

export const PosProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Storage is async (IndexedDB), so state starts empty and is hydrated from the
  // database in the effect below. `hydrated` gates the write-through effects so
  // the initial empty arrays can't overwrite stored data before it loads.
  const [config, setConfig] = useState<PosConfig>(INITIAL_CONFIG);
  const [appSettings, setAppSettings] = useState<AppToggleSettings>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<Transaction[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [salesmen, setSalesmen] = useState<Salesman[]>([]);
  const [vehicles, setVehicles] = useState<TransportInfo[]>([]);
  const [onlineOrders, setOnlineOrders] = useState<OnlineOrder[]>([]);
  const [servicePoints, setServicePoints] = useState<ServicePoint[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [unitGroups, setUnitGroups] = useState<UnitGroup[]>([]);
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);
  const [cashflow, setCashflow] = useState<CashFlowEntry[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [activeShift, setActiveShiftState] = useState<ActiveShift | null>(null);
  const hydrated = useRef(false);

  // Load every collection once on mount. Reads run in parallel; nothing is
  // persisted back until this completes (see the `hydrated` guard).
  useEffect(() => {
    let alive = true;
    (async () => {
      const [
        cfg, appS, prods, sls, custs, sups, sm, veh, oo, sp, cps, rcp, cats, ug, attrs, cf, mv, as,
      ] = await Promise.all([
        db.getConfig(), db.getAppSettings(), db.getProducts(), db.getSales(), db.getCustomers(),
        db.getSuppliers(), db.getSalesmen(), db.getVehicles(), db.getOnlineOrders(),
        db.getServicePoints(), db.getCoupons(), db.getRecipes(), db.getCategories(),
        db.getUnitGroups(), db.getAttributes(), db.getCashFlow(), db.getMovements(),
        db.getActiveShift(),
      ]);
      if (!alive) return;
      setConfig(cfg);
      setAppSettings(appS);
      setProducts(prods);
      setSales(sls);
      setCustomers(custs);
      setSuppliers(sups);
      setSalesmen(sm);
      setVehicles(veh);
      setOnlineOrders(oo);
      setServicePoints(sp);
      setCoupons(cps);
      setRecipes(rcp);
      setCategories(cats);
      setUnitGroups(ug);
      setAttributes(attrs);
      setCashflow(cf);
      setMovements(mv);
      setActiveShiftState(as);
      hydrated.current = true;
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Persistence no longer happens via whole-array write-through here. Every
  // mutator below emits an *operation* (services/ops.ts) that both persists the
  // change to IndexedDB and — once a sync transport is connected (Phase 1) —
  // ships it to the LAN server. That op path is also how changes arrive FROM
  // other terminals: when a remote op is applied it fires COLLECTION_CHANGED,
  // and this effect reloads just that collection into component state so the UI
  // reflects what another device did. Local mutations set their own state
  // directly (applyOp runs with notify=false for them), so this only reacts to
  // remote changes.
  useEffect(() => {
    const reloaders: Record<string, () => void> = {
      config: () => db.getConfig().then(setConfig),
      appSettings: () => db.getAppSettings().then(setAppSettings),
      products: () => db.getProducts().then(setProducts),
      sales: () => db.getSales().then(setSales),
      customers: () => db.getCustomers().then(setCustomers),
      suppliers: () => db.getSuppliers().then(setSuppliers),
      salesmen: () => db.getSalesmen().then(setSalesmen),
      vehicles: () => db.getVehicles().then(setVehicles),
      onlineOrders: () => db.getOnlineOrders().then(setOnlineOrders),
      servicePoints: () => db.getServicePoints().then(setServicePoints),
      coupons: () => db.getCoupons().then(setCoupons),
      recipes: () => db.getRecipes().then(setRecipes),
      categories: () => db.getCategories().then(setCategories),
      unitGroups: () => db.getUnitGroups().then(setUnitGroups),
      attributes: () => db.getAttributes().then(setAttributes),
      cashflow: () => db.getCashFlow().then(setCashflow),
      movements: () => db.getMovements().then(setMovements),
      activeShift: () => db.getActiveShift().then(setActiveShiftState),
    };
    const onChanged = (e: Event) => {
      const collection = (e as CustomEvent<{ collection: string }>).detail?.collection;
      reloaders[collection]?.();
    };
    window.addEventListener(COLLECTION_CHANGED, onChanged);
    return () => window.removeEventListener(COLLECTION_CHANGED, onChanged);
  }, []);

  // ---- Config ----
  const updateConfig = useCallback((next: PosConfig) => {
    opSet('config', next);
    setConfig(next);
  }, []);

  // ---- App settings (feature toggles) ----
  const updateAppSettings = useCallback((next: AppToggleSettings) => {
    opSet('appSettings', next);
    setAppSettings(next);
  }, []);

  // Tint the whole UI with the saved theme colour (and on first load).
  useEffect(() => {
    applyTheme(choiceOf(appSettings, 'theme_color'));
  }, [appSettings]);

  const isOnSetting = useCallback((key: string) => isOn(appSettings, key), [appSettings]);
  const choiceSetting = useCallback((key: string) => choiceOf(appSettings, key), [appSettings]);

  // ---- Products ----
  // Every mutation also writes the Product Master change trail
  // (db.logProductHistory), shown via the History button on the Products page.
  const addProduct = useCallback((product: Omit<Product, 'id'>) => {
    const created: Product = { ...product, id: db.newId() };
    opUpsert('products', created.id, created);
    db.logProductHistory({ productId: created.id, productName: created.name, action: 'created' });
    setProducts((prev) => [...prev, created]);
  }, []);

  const updateProduct = useCallback((product: Product) => {
    opUpsert('products', product.id, product);
    setProducts((prev) => {
      const before = prev.find((p) => p.id === product.id);
      if (before) {
        const changes = db.diffProducts(before, product);
        if (changes.length > 0) {
          db.logProductHistory({ productId: product.id, productName: product.name, action: 'updated', changes });
        }
      }
      return prev.map((p) => (p.id === product.id ? product : p));
    });
  }, []);

  const deleteProduct = useCallback((id: string) => {
    opDelete('products', id);
    setProducts((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) db.logProductHistory({ productId: id, productName: target.name, action: 'deleted' });
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const getProductByBarcode = useCallback(
    (barcode: string) => products.find((p) => p.barcode === barcode),
    [products]
  );

  // ---- Customers ----
  const addCustomer = useCallback((customer: Omit<Customer, 'id'>): Customer => {
    const created: Customer = { ...customer, id: db.newId() };
    opUpsert('customers', created.id, created);
    setCustomers((prev) => [...prev, created]);
    return created;
  }, []);

  const updateCustomer = useCallback((customer: Customer) => {
    opUpsert('customers', customer.id, customer);
    setCustomers((prev) => prev.map((c) => (c.id === customer.id ? customer : c)));
  }, []);

  const deleteCustomer = useCallback((id: string) => {
    opDelete('customers', id);
    setCustomers((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // ---- Suppliers ----
  const addSupplier = useCallback((supplier: Omit<Supplier, 'id'>) => {
    const created: Supplier = { ...supplier, id: db.newId() };
    opUpsert('suppliers', created.id, created);
    setSuppliers((prev) => [...prev, created]);
  }, []);

  const updateSupplier = useCallback((supplier: Supplier) => {
    opUpsert('suppliers', supplier.id, supplier);
    setSuppliers((prev) => prev.map((s) => (s.id === supplier.id ? supplier : s)));
  }, []);

  const deleteSupplier = useCallback((id: string) => {
    opDelete('suppliers', id);
    setSuppliers((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // ---- Salesmen / employees ----
  const addSalesman = useCallback((salesman: Omit<Salesman, 'id'>) => {
    const created: Salesman = { ...salesman, id: db.newId() };
    opUpsert('salesmen', created.id, created);
    setSalesmen((prev) => [...prev, created]);
  }, []);
  const updateSalesman = useCallback((salesman: Salesman) => {
    opUpsert('salesmen', salesman.id, salesman);
    setSalesmen((prev) => prev.map((s) => (s.id === salesman.id ? salesman : s)));
  }, []);
  const deleteSalesman = useCallback((id: string) => {
    opDelete('salesmen', id);
    setSalesmen((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // ---- Transport vehicles ----
  // Upsert by vehicle number so the most recent detail wins (case-insensitive).
  const saveVehicle = useCallback((info: TransportInfo) => {
    const key = info.vehicleNo.trim().toUpperCase();
    if (!key) return;
    const row: TransportInfo = { ...info, vehicleNo: info.vehicleNo.trim() };
    opUpsert('vehicles', key, row);
    setVehicles((prev) => {
      const rest = prev.filter((v) => v.vehicleNo.trim().toUpperCase() !== key);
      return [row, ...rest];
    });
  }, []);

  // ---- Online orders ----
  const addOnlineOrder = useCallback(
    (order: Omit<OnlineOrder, 'id' | 'createdAt' | 'updatedAt'>): OnlineOrder => {
      const now = new Date().toISOString();
      const created: OnlineOrder = { ...order, id: db.newId(), createdAt: now, updatedAt: now };
      opUpsert('onlineOrders', created.id, created);
      setOnlineOrders((prev) => [created, ...prev]);
      return created;
    },
    []
  );
  const updateOnlineOrder = useCallback((order: OnlineOrder) => {
    const updated: OnlineOrder = { ...order, updatedAt: new Date().toISOString() };
    opUpsert('onlineOrders', updated.id, updated);
    setOnlineOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  }, []);
  const deleteOnlineOrder = useCallback((id: string) => {
    opDelete('onlineOrders', id);
    setOnlineOrders((prev) => prev.filter((o) => o.id !== id));
  }, []);

  // ---- Service points ----
  const addServicePoint = useCallback((sp: Omit<ServicePoint, 'id'>) => {
    const created: ServicePoint = { ...sp, id: db.newId() };
    opUpsert('servicePoints', created.id, created);
    setServicePoints((prev) => [...prev, created]);
  }, []);
  const updateServicePoint = useCallback((sp: ServicePoint) => {
    opUpsert('servicePoints', sp.id, sp);
    setServicePoints((prev) => prev.map((x) => (x.id === sp.id ? sp : x)));
  }, []);
  const deleteServicePoint = useCallback((id: string) => {
    opDelete('servicePoints', id);
    setServicePoints((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // ---- Coupons ----
  const addCoupon = useCallback((coupon: Omit<Coupon, 'id'>) => {
    const created: Coupon = { ...coupon, id: db.newId() };
    opUpsert('coupons', created.id, created);
    setCoupons((prev) => [...prev, created]);
  }, []);
  const updateCoupon = useCallback((coupon: Coupon) => {
    opUpsert('coupons', coupon.id, coupon);
    setCoupons((prev) => prev.map((c) => (c.id === coupon.id ? coupon : c)));
  }, []);
  const deleteCoupon = useCallback((id: string) => {
    opDelete('coupons', id);
    setCoupons((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // ---- Recipes ----
  const addRecipe = useCallback((recipe: Omit<Recipe, 'id'>) => {
    const created: Recipe = { ...recipe, id: db.newId() };
    opUpsert('recipes', created.id, created);
    setRecipes((prev) => [...prev, created]);
  }, []);
  const updateRecipe = useCallback((recipe: Recipe) => {
    opUpsert('recipes', recipe.id, recipe);
    setRecipes((prev) => prev.map((r) => (r.id === recipe.id ? recipe : r)));
  }, []);
  const deleteRecipe = useCallback((id: string) => {
    opDelete('recipes', id);
    setRecipes((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // ---- Categories ----
  const addCategory = useCallback((category: Omit<Category, 'id'>) => {
    const created: Category = { ...category, id: db.newId() };
    opUpsert('categories', created.id, created);
    setCategories((prev) => [...prev, created]);
  }, []);

  const updateCategory = useCallback((category: Category) => {
    opUpsert('categories', category.id, category);
    setCategories((prev) => prev.map((c) => (c.id === category.id ? category : c)));
  }, []);

  const deleteCategory = useCallback((id: string) => {
    opDelete('categories', id);
    setCategories((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const addUnitGroup = useCallback((group: Omit<UnitGroup, 'id'>) => {
    const created: UnitGroup = { ...group, id: db.newId() };
    opUpsert('unitGroups', created.id, created);
    setUnitGroups((prev) => [...prev, created]);
  }, []);

  const updateUnitGroup = useCallback((group: UnitGroup) => {
    opUpsert('unitGroups', group.id, group);
    setUnitGroups((prev) => prev.map((g) => (g.id === group.id ? group : g)));
  }, []);

  const deleteUnitGroup = useCallback((id: string) => {
    opDelete('unitGroups', id);
    setUnitGroups((prev) => prev.filter((g) => g.id !== id));
  }, []);

  // ---- Attribute master ----
  const addAttribute = useCallback((attribute: Omit<AttributeDef, 'id'>) => {
    const created: AttributeDef = { ...attribute, id: db.newId() };
    opUpsert('attributes', created.id, created);
    setAttributes((prev) => [...prev, created]);
  }, []);

  const updateAttribute = useCallback((attribute: AttributeDef) => {
    opUpsert('attributes', attribute.id, attribute);
    setAttributes((prev) => prev.map((a) => (a.id === attribute.id ? attribute : a)));
  }, []);

  // Removing an attribute also strips its values from every product that
  // carries it, so the master and the products never drift apart.
  const deleteAttribute = useCallback((id: string) => {
    opDelete('attributes', id);
    setAttributes((prev) => prev.filter((a) => a.id !== id));
    setProducts((prev) =>
      prev.map((p) => {
        if (!p.attributes?.some((a) => a.attributeId === id)) return p;
        const next = { ...p, attributes: p.attributes.filter((a) => a.attributeId !== id) };
        opUpsert('products', next.id, next);
        return next;
      })
    );
  }, []);

  // ---- Sales ----
  // Build a movement entry (pure — id + timestamp stamped here).
  const makeMovement = useCallback(
    (m: Omit<StockMovement, 'id' | 'timestamp'>): StockMovement => ({
      ...m,
      id: db.newId(),
      timestamp: new Date().toISOString(),
    }),
    []
  );

  // Persist a burst of movements as append (upsert) ops and mirror them into
  // state. Each movement is its own op so two terminals logging movements at the
  // same time merge instead of overwriting.
  const emitMovements = useCallback((mvs: StockMovement[]) => {
    mvs.forEach((m) => opUpsert('movements', m.id, m));
    if (mvs.length > 0) setMovements((prev) => [...prev, ...mvs]);
  }, []);

  const logMovement = useCallback(
    (m: Omit<StockMovement, 'id' | 'timestamp'>) => {
      emitMovements([makeMovement(m)]);
    },
    [emitMovements, makeMovement]
  );

  const recordSale = useCallback(
    async (sale: Transaction): Promise<Transaction> => {
      // Assign the next sequential bill number at the moment of recording, so
      // numbering stays gap-free even if some checkouts are cancelled. GST bills
      // also get a separate invoice number; estimate / non-GST bills do not.
      const isGstBill = sale.gstFlag !== false && !sale.estimate;
      // Stamp the sale with the open shift session, auto-opening today's first
      // shift if none is running (Android opens the shift with the day). If this
      // sale opened a new shift, share it so other counters adopt the same one.
      const prevShift = await db.getActiveShift();
      const shift = await db.ensureActiveShift();
      if (!prevShift || prevShift.id !== shift.id) await opSet('activeShift', shift);
      setActiveShiftState(shift);
      const finalized: Transaction = {
        ...sale,
        billNo: await db.nextBillNo(),
        invoiceNumber: isGstBill ? sale.invoiceNumber ?? (await db.nextInvoiceNo()) : undefined,
        shiftId: sale.shiftId ?? shift.id,
      };
      // Persist the sale first (awaited) so a failed/quota-blocked write throws
      // before it is ever shown as recorded. The sale is an upsert op so it also
      // replicates to other terminals once sync is connected.
      await opUpsert('sales', finalized.id, finalized);
      setSales((prev) => [...prev, finalized]);

      const newMovements: StockMovement[] = finalized.items.map((i) =>
        makeMovement({
          productId: i.productId,
          productName: i.name,
          variantId: i.variantId,
          type: 'out',
          reason: 'sale',
          quantity: i.quantity,
          note: finalized.billNo,
        })
      );

      // Stock deltas: the sold items, plus recipe consumption — for any sold item
      // that is a recipe (menu) product, deduct each ingredient's stock by
      // ingredient-qty × quantity sold. All applied as one commutative
      // adjustStock op so simultaneous sales on two terminals both count.
      const stockDeltas: { productId: string; variantId?: string; delta: number }[] =
        finalized.items.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          delta: -i.quantity,
        }));
      const recipeList = await db.getRecipes();
      finalized.items.forEach((i) => {
        const recipe = recipeList.find((r) => r.productId === i.productId);
        recipe?.ingredients.forEach((ing) => {
          stockDeltas.push({ productId: ing.productId, delta: -ing.quantity * i.quantity });
          newMovements.push(
            makeMovement({
              productId: ing.productId,
              productName: ing.name,
              type: 'out',
              reason: 'sale',
              quantity: ing.quantity * i.quantity,
              note: `Recipe · ${finalized.billNo}`,
            })
          );
        });
      });
      await opAdjustStock(stockDeltas);
      // Best-seller ordering (keyCount) stays a local-only bump for now.
      await db.bumpKeyCounts(finalized.items);
      setProducts(await db.getProducts());
      emitMovements(newMovements);

      // Apply customer-account side-effects of the tender: a 'credit' split
      // adds to the customer's outstanding balance, and loyalty points earned
      // / redeemed on this bill adjust their points balance. Read the current
      // record so the op carries the authoritative new balance.
      const creditAmt = (finalized.payments ?? [])
        .filter((p) => p.method === 'credit')
        .reduce((s, p) => s + p.amount, 0);
      const earned = finalized.loyaltyEarned ?? 0;
      const redeemed = finalized.loyaltyRedeemed ?? 0;
      if (finalized.customerId && (creditAmt > 0 || earned > 0 || redeemed > 0)) {
        const current = (await db.getCustomers()).find((c) => c.id === finalized.customerId);
        if (current) {
          const updated: Customer = {
            ...current,
            creditBalance: Math.max(0, (current.creditBalance ?? 0) + creditAmt),
            loyaltyPoints: Math.max(0, (current.loyaltyPoints ?? 0) + earned - redeemed),
          };
          await opUpsert('customers', updated.id, updated);
          setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        }
      }
      db.logEvent({
        type: 'sale',
        detail: `Bill ${finalized.billNo}${finalized.estimate ? ' (estimate)' : ''} · ${finalized.total.toFixed(2)}`,
      });
      return finalized;
    },
    [makeMovement, emitMovements]
  );

  // Edit Bill: replace a stored sale and reconcile stock by the per-product
  // quantity delta (old − new), so reducing/removing a line returns stock and
  // adding/raising a line consumes it. A matching 'adjustment' movement is
  // logged per changed product for the audit trail.
  const updateSale = useCallback(
    async (updated: Transaction) => {
      const current = await db.getSales();
      const old = current.find((s) => s.id === updated.id);
      await opUpsert('sales', updated.id, updated);
      setSales((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      db.logEvent({ type: 'edit', detail: `Edited bill ${updated.billNo}` });
      if (!old) return;

      const deltas = new Map<string, { name: string; delta: number }>();
      old.items.forEach((i) =>
        deltas.set(i.productId, { name: i.name, delta: (deltas.get(i.productId)?.delta ?? 0) + i.quantity })
      );
      updated.items.forEach((i) =>
        deltas.set(i.productId, { name: i.name, delta: (deltas.get(i.productId)?.delta ?? 0) - i.quantity })
      );

      const adj = Array.from(deltas.entries())
        .filter(([, v]) => v.delta !== 0)
        .map(([productId, v]) => ({ productId, name: v.name, delta: v.delta }));
      if (adj.length === 0) return;

      await opAdjustStock(adj.map(({ productId, delta }) => ({ productId, delta })));
      setProducts(await db.getProducts());
      adj.forEach(({ productId, name, delta }) =>
        logMovement({
          productId,
          productName: name,
          type: delta > 0 ? 'in' : 'out',
          reason: 'adjustment',
          quantity: Math.abs(delta),
          note: `Edit bill ${updated.billNo}`,
        })
      );
    },
    [logMovement]
  );

  const voidSale = useCallback(async (id: string) => {
    const current = await db.getSales();
    const target = current.find((s) => s.id === id);
    if (!target) return;
    const cancelled: Transaction = { ...target, status: 'cancelled' as const };
    await opUpsert('sales', id, cancelled);
    setSales((prev) => prev.map((s) => (s.id === id ? cancelled : s)));
    db.logEvent({ type: 'void', detail: `Voided bill ${target.billNo} · ${target.total.toFixed(2)}` });
  }, []);

  const recordSaleReturn = useCallback(
    async (originalBillNo: string, returnedItems: Transaction['items'], note?: string) => {
      const subtotal = returnedItems.reduce((s, i) => s + i.total, 0);
      const tax = returnedItems.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0);
      const ret: Transaction = {
        id: db.newId(),
        billNo: `R${Date.now()}`,
        items: returnedItems,
        subtotal: -subtotal,
        tax: -tax,
        discount: 0,
        total: -(subtotal + tax),
        paymentMethod: 'cash',
        timestamp: new Date().toISOString(),
        status: 'completed',
        udn: note ?? `Return of ${originalBillNo}`,
      };
      await opUpsert('sales', ret.id, ret);
      setSales((prev) => [...prev, ret]);
      // Returned items go back into stock.
      await opAdjustStock(
        returnedItems.map((i) => ({ productId: i.productId, variantId: i.variantId, delta: i.quantity }))
      );
      setProducts(await db.getProducts());
      returnedItems.forEach((i) =>
        logMovement({
          productId: i.productId,
          productName: i.name,
          variantId: i.variantId,
          type: 'in',
          reason: 'sale_return',
          quantity: i.quantity,
          note: originalBillNo,
        })
      );
    },
    [logMovement]
  );

  // ---- Inventory ----
  const receiveGoods = useCallback(
    async (lines: GoodsReceiveLine[], supplierId?: string, note?: string) => {
      await opAdjustStock(
        lines.map((l) => ({ productId: l.productId, variantId: l.variantId, delta: l.quantity }))
      );
      const all = await db.getProducts();
      setProducts(all);
      lines.forEach((l) =>
        logMovement({
          productId: l.productId,
          productName: all.find((p) => p.id === l.productId)?.name ?? l.productId,
          variantId: l.variantId,
          variantName: l.variantName,
          type: 'in',
          reason: 'goods_received',
          quantity: l.quantity,
          supplierId,
          note,
        })
      );
    },
    [logMovement]
  );

  const stockOut = useCallback(
    async (lines: GoodsReceiveLine[], reason: string) => {
      await opAdjustStock(
        lines.map((l) => ({ productId: l.productId, variantId: l.variantId, delta: -l.quantity }))
      );
      const all = await db.getProducts();
      setProducts(all);
      lines.forEach((l) =>
        logMovement({
          productId: l.productId,
          productName: all.find((p) => p.id === l.productId)?.name ?? l.productId,
          variantId: l.variantId,
          variantName: l.variantName,
          type: 'out',
          reason: 'stock_out',
          quantity: l.quantity,
          note: reason,
        })
      );
    },
    [logMovement]
  );

  // ---- CashFlow ----
  const addCashFlow = useCallback(async (entry: Omit<CashFlowEntry, 'id' | 'timestamp'>) => {
    const full: CashFlowEntry = { ...entry, id: db.newId(), timestamp: new Date().toISOString() };
    await opUpsert('cashflow', full.id, full);
    setCashflow((prev) => [...prev, full]);
    // Credit received reduces the customer's outstanding balance; an advance
    // adds to the customer's advance balance (Android AdvanceFunction). Read the
    // current record so the op carries the authoritative new balance.
    if (entry.customerId && (entry.type === 'credit_received' || entry.type === 'advance')) {
      const current = (await db.getCustomers()).find((c) => c.id === entry.customerId);
      if (current) {
        const updated: Customer =
          entry.type === 'credit_received'
            ? { ...current, creditBalance: (current.creditBalance ?? 0) - entry.amount }
            : { ...current, advance: (current.advance ?? 0) + entry.amount };
        await opUpsert('customers', updated.id, updated);
        setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      }
    }
  }, []);

  // ---- Shift sessions ----
  const openShift = useCallback(async (openingCash: number): Promise<ActiveShift> => {
    const shift = await db.openShiftSession(openingCash);
    // Share the open shift with every terminal so bills across counters stamp
    // the same shop-wide shift (opSet re-persists via applyOp, idempotently).
    await opSet('activeShift', shift);
    setActiveShiftState(shift);
    db.logEvent({ type: 'shift_open', detail: `Opened shift ${shift.id}` });
    return shift;
  }, []);

  const closeShift = useCallback(
    async (record: ShiftRecord, startNext: boolean, nextOpeningCash?: number) => {
      // The closed shift record replicates to other terminals (consolidated
      // shift reports); the active shift is shared so closing frees every counter.
      await opUpsert('shifts', record.id, record);
      db.logEvent({
        type: 'shift_close',
        detail: `Closed shift ${record.dayId ?? ''}-${record.shiftNo ?? ''} · sales ${record.totalSales.toFixed(2)}`,
      });
      if (startNext) {
        const next = await db.openShiftSession(nextOpeningCash ?? record.expectedCash);
        await opSet('activeShift', next);
        setActiveShiftState(next);
        db.logEvent({ type: 'shift_open', detail: `Opened shift ${next.id}` });
      } else {
        await opSet('activeShift', null);
        setActiveShiftState(null);
      }
    },
    []
  );

  const formatMoney = useCallback(
    (amount: number) => `${config.currencySymbol}${amount.toFixed(2)}`,
    [config.currencySymbol]
  );

  const value = useMemo<PosContextValue>(
    () => ({
      config,
      appSettings,
      isOn: isOnSetting,
      choice: choiceSetting,
      updateAppSettings,
      products,
      sales,
      customers,
      suppliers,
      salesmen,
      categories,
      cashflow,
      movements,
      updateConfig,
      addProduct,
      updateProduct,
      deleteProduct,
      getProductByBarcode,
      addCustomer,
      updateCustomer,
      deleteCustomer,
      addSupplier,
      updateSupplier,
      deleteSupplier,
      addSalesman,
      updateSalesman,
      deleteSalesman,
      vehicles,
      saveVehicle,
      onlineOrders,
      addOnlineOrder,
      updateOnlineOrder,
      deleteOnlineOrder,
      servicePoints,
      addServicePoint,
      updateServicePoint,
      deleteServicePoint,
      coupons,
      addCoupon,
      updateCoupon,
      deleteCoupon,
      recipes,
      addRecipe,
      updateRecipe,
      deleteRecipe,
      addCategory,
      updateCategory,
      deleteCategory,
      unitGroups,
      addUnitGroup,
      updateUnitGroup,
      deleteUnitGroup,
      attributes,
      addAttribute,
      updateAttribute,
      deleteAttribute,
      recordSale,
      updateSale,
      voidSale,
      recordSaleReturn,
      receiveGoods,
      stockOut,
      addCashFlow,
      activeShift,
      openShift,
      closeShift,
      formatMoney,
    }),
    [
      config,
      appSettings,
      isOnSetting,
      choiceSetting,
      updateAppSettings,
      products,
      sales,
      customers,
      suppliers,
      salesmen,
      categories,
      cashflow,
      movements,
      updateConfig,
      addProduct,
      updateProduct,
      deleteProduct,
      getProductByBarcode,
      addCustomer,
      updateCustomer,
      deleteCustomer,
      addSupplier,
      updateSupplier,
      deleteSupplier,
      addSalesman,
      updateSalesman,
      deleteSalesman,
      vehicles,
      saveVehicle,
      onlineOrders,
      addOnlineOrder,
      updateOnlineOrder,
      deleteOnlineOrder,
      servicePoints,
      addServicePoint,
      updateServicePoint,
      deleteServicePoint,
      coupons,
      addCoupon,
      updateCoupon,
      deleteCoupon,
      recipes,
      addRecipe,
      updateRecipe,
      deleteRecipe,
      addCategory,
      updateCategory,
      deleteCategory,
      unitGroups,
      addUnitGroup,
      updateUnitGroup,
      deleteUnitGroup,
      attributes,
      addAttribute,
      updateAttribute,
      deleteAttribute,
      recordSale,
      updateSale,
      voidSale,
      recordSaleReturn,
      receiveGoods,
      stockOut,
      addCashFlow,
      activeShift,
      openShift,
      closeShift,
      formatMoney,
    ]
  );

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
};

export function usePos(): PosContextValue {
  const ctx = useContext(PosContext);
  if (!ctx) {
    throw new Error('usePos must be used within a PosProvider');
  }
  return ctx;
}
